import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { RefreshCw, Check, X } from 'lucide-react';

export default function Exchanges() {
  const [exchanges, setExchanges] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { axiosInstance, user } = useAuth();

  useEffect(() => {
    fetchExchanges();
    fetchUsers();
  }, []);

  const fetchExchanges = async () => {
    try {
      const response = await axiosInstance.get('/exchanges');
      setExchanges(response.data);
    } catch (error) {
      toast.error('Échec du chargement des échanges');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const getUserName = (userId) => {
    const u = users.find(usr => usr.id === userId);
    return u ? `${u.first_name} ${u.last_name}` : 'Inconnu';
  };

  const handleRespond = async (exchangeId, accept) => {
    try {
      await axiosInstance.put(`/exchanges/${exchangeId}/respond`, null, {
        params: { accept }
      });
      toast.success(accept ? 'Échange accepté' : 'Échange refusé');
      fetchExchanges();
    } catch (error) {
      toast.error('Échec de la réponse');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-orange-500 text-white',
      accepted: 'bg-green-500 text-white',
      rejected: 'bg-red-500 text-white'
    };
    const labels = {
      pending: 'En attente',
      accepted: 'Accepté',
      rejected: 'Refusé'
    };
    return <Badge className={variants[status]}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <Layout title="Échanges de prestations">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Échanges de prestations">
      <div className="space-y-4" data-testid="exchanges-list">
        {exchanges.length === 0 ? (
          <Card className="glass-card border-amber-200">
            <CardContent className="py-12 text-center text-gray-500">
              Aucun échange trouvé
            </CardContent>
          </Card>
        ) : (
          exchanges.map((exchange) => {
            const isReceiver = exchange.to_user_id === user.id;
            const otherUserId = isReceiver ? exchange.from_user_id : exchange.to_user_id;
            
            return (
              <Card key={exchange.id} className="glass-card border-amber-200" data-testid={`exchange-card-${exchange.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="text-amber-600" size={20} />
                        <h3 className="text-lg font-semibold text-amber-900">
                          {isReceiver ? 'Demande reçue de' : 'Demande envoyée à'} {getUserName(otherUserId)}
                        </h3>
                        {getStatusBadge(exchange.status)}
                      </div>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Raison:</span> {exchange.reason}
                      </p>
                      <p className="text-xs text-gray-500">
                        Créé le {new Date(exchange.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>

                    {isReceiver && exchange.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleRespond(exchange.id, true)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`accept-exchange-${exchange.id}`}
                        >
                          <Check size={16} className="mr-1" />
                          Accepter
                        </Button>
                        <Button
                          onClick={() => handleRespond(exchange.id, false)}
                          variant="destructive"
                          data-testid={`reject-exchange-${exchange.id}`}
                        >
                          <X size={16} className="mr-1" />
                          Refuser
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </Layout>
  );
}